import { css } from '@emotion/css';
import { useTheme } from '@mui/material/styles';
import React from 'react';

/*
 * `makeStyles` and `withStyles`, on emotion, with MUI 4's signatures.
 *
 * MUI 9 has no styling engine of the JSS kind and `@mui/styles` stops at 6, so
 * the forty files that declare styles this way had two futures: rewrite each one
 * to `styled()`/`sx`, or keep them and reimplement the two functions they call.
 *
 * This is the second. Not to avoid work - the rewrite is mechanical - but
 * because of what this branch promises: the screens do not change. Eighty-three
 * style declarations rewritten by hand is eighty-three chances to move a
 * padding, and no reviewer can tell a faithful translation from a slightly
 * wrong one by reading it. Keeping the declarations byte-identical moves the
 * whole question into one file, where it can be read once.
 *
 * What MUI 4 did that this does too:
 *   - a spec object, or a function of the theme
 *   - nested `&` selectors, which emotion handles natively
 *   - `classes` from props merged over the generated ones (`withStyles`)
 *   - refs forwarded through the HOC - `BaseTable` is `forwardRef` and every
 *     caller passes `tableRef`, so dropping the ref would break every table
 *
 * What it deliberately does NOT do: `$ruleName` cross-references. JSS resolved
 * `'&$selected'` to the generated class of the `selected` rule; emotion has no
 * such notion. Two files used it and they now name the real class MUI emits,
 * `&.Mui-selected`, which is what the rule always meant.
 */

const build = (spec, theme) => {
  const rules = typeof spec === 'function' ? spec(theme) : spec;
  const out = {};
  for (const name of Object.keys(rules)) {
    // `&&` doubles the class in the selector - `.css-x.css-x` - which beats a
    // component's own single-class styles by SPECIFICITY rather than by order
    // of injection. That is what JSS gave `withStyles` in MUI 4 and what a bare
    // emotion class does not: MUI 9's styles are inserted after ours, so
    // `AppBar`'s `color="primary"` painted the header orange over a rule that
    // said white, on every screen. Nothing in the declarations changes; the
    // selector they land in gets one more class.
    out[name] = css({ '&&': rules[name] });
  }
  return out;
};

export const makeStyles = spec => () => build(spec, useTheme());

export const withStyles = spec => Component => {
  const Styled = React.forwardRef((props, ref) => {
    const generated = build(spec, useTheme());
    const classes = props.classes ? { ...generated, ...props.classes } : generated;
    return <Component ref={ref} {...props} classes={classes} />;
  });
  Styled.displayName = `withStyles(${Component.displayName || Component.name || 'Component'})`;
  return Styled;
};

export default withStyles;
